import React, { HTMLAttributes, forwardRef } from 'react';

type SkeletonVariant = 'text' | 'circular' | 'rectangular' | 'rounded';

interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  variant?: SkeletonVariant;
  width?: string | number;
  height?: string | number;
  lines?: number;
  animated?: boolean;
}

const variantClasses: Record<SkeletonVariant, string> = {
  text: 'rounded',
  circular: 'rounded-full',
  rectangular: 'rounded-none',
  rounded: 'rounded-au-base',
};

const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  (
    {
      variant = 'text',
      width,
      height,
      lines = 1,
      animated = true,
      className = '',
      style,
      ...props
    },
    ref
  ) => {
    const baseClasses = `
      ${animated ? 'au-skeleton' : 'bg-au-surface-sunken'}
      ${variantClasses[variant]}
    `;

    const computedStyle = {
      width: typeof width === 'number' ? `${width}px` : width,
      height:
        typeof height === 'number'
          ? `${height}px`
          : height || (variant === 'text' ? '1em' : undefined),
      ...style,
    };

    if (lines > 1) {
      return (
        <div ref={ref} className={`space-y-2 ${className}`} {...props}>
          {Array.from({ length: lines }).map((_, index) => (
            <div
              key={index}
              className={`${baseClasses} h-4`}
              style={{
                width: index === lines - 1 ? '80%' : '100%',
              }}
            />
          ))}
        </div>
      );
    }

    return (
      <div ref={ref} className={`${baseClasses} ${className}`} style={computedStyle} {...props} />
    );
  }
);

Skeleton.displayName = 'Skeleton';

// Preset Skeleton Components
const SkeletonText: React.FC<{ lines?: number; className?: string }> = ({
  lines = 3,
  className = '',
}) => <Skeleton variant="text" lines={lines} className={className} />;

const SkeletonAvatar: React.FC<{
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}> = ({ size = 'md', className = '' }) => {
  const sizeMap = { sm: 32, md: 40, lg: 56 };
  return (
    <Skeleton
      variant="circular"
      width={sizeMap[size]}
      height={sizeMap[size]}
      className={className}
    />
  );
};

const SkeletonButton: React.FC<{
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}> = ({ size = 'md', className = '' }) => {
  const heightMap = { sm: 32, md: 40, lg: 48 };
  return <Skeleton variant="rounded" width={100} height={heightMap[size]} className={className} />;
};

const SkeletonCard: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`au-card p-6 ${className}`}>
    <div className="flex items-center mb-4">
      <SkeletonAvatar size="md" />
      <div className="ml-4 flex-1">
        <Skeleton variant="text" width="60%" height={16} className="mb-2" />
        <Skeleton variant="text" width="40%" height={12} />
      </div>
    </div>
    <SkeletonText lines={3} />
  </div>
);

export { Skeleton, SkeletonText, SkeletonAvatar, SkeletonButton, SkeletonCard };
export default Skeleton;
